"use client";

/* eslint-disable react-hooks/rules-of-hooks */

import PowerOnPro from "@/components/icons/poweron-pro";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOrganizationStore } from "@/stores/organization";
import { daysAgo, getInitials, getUserName } from "@/utils/utils";
import { ColumnDef } from "@tanstack/react-table";
import { AtSign, Clock, Crown, IdCard, LoaderCircle } from "lucide-react";
import { useRef, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { Member } from "@/types/member";
import {
  DEFAULT_DEBOUNCE_DELAY,
  TAILWIND_SHAKE_ANIMATION_DELAY,
} from "@/lib/constants";

export const columns = (
  onToggleSubscription: (member: Member, checked: boolean) => void,
  loadingMemberId: string | null = null,
): ColumnDef<Member>[] => [
  {
    accessorKey: "name",
    header: () => (
      <div className="flex items-center gap-1">
        <IdCard size={14} className="mt-0.5 text-muted" />
        Name
      </div>
    ),
    cell: ({ row }) => {
      const { organization } = useOrganizationStore();
      const name = getUserName(row.original.name, row.original.email);

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-7 rounded-full">
            <AvatarImage
              src={row.original.avatar ? `/avatars/${row.original.avatar}` : ""}
              alt={`Avatar for ${name}`}
            />
            <AvatarFallback className="rounded-full bg-card pb-0.5 text-xs font-medium text-muted">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-none">
            <span className="flex items-center truncate py-2 font-semibold text-gray-1100 dark:text-gray-100">
              {name}
              {row.original.userId === organization?.ownerId && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Crown
                      className="ml-1 flex-shrink-0 text-amber-400"
                      size={14}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center">
                    Organization Owner
                  </TooltipContent>
                </Tooltip>
              )}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "email",
    header: () => (
      <div className="flex items-center gap-1">
        <AtSign size={14} className="mt-0.5 text-muted" />
        Email
      </div>
    ),
    cell: ({ row }) => {
      const resetTimerRef = useRef<NodeJS.Timeout | null>(null);
      const [tooltipText, setTooltipText] = useState("Copy Email");
      const [tooltipBg, setTooltipBg] = useState(
        "bg-[hsl(var(--popover))] text-primary-foreground",
      );
      const [open, setOpen] = useState(false);

      const setOriginalState = () => {
        setTimeout(() => {
          setTooltipText("Copy Email");
          setTooltipBg("bg-[hsl(var(--popover))] text-primary-foreground");
        }, 500);
      };
      const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        navigator.clipboard.writeText(row.original.email);

        setTooltipText("Copied!");
        setTooltipBg("bg-emerald-500 text-white");
        setOpen(true);

        if (resetTimerRef.current) {
          clearTimeout(resetTimerRef.current);
        }

        resetTimerRef.current = setTimeout(() => {
          setOpen(false);
          setOriginalState();
          resetTimerRef.current = null;
        }, 2000);
      };

      return (
        <TooltipProvider disableHoverableContent delayDuration={0}>
          <div className="flex items-center gap-2">
            <Tooltip open={open} onOpenChange={setOpen}>
              <TooltipTrigger asChild>
                <div className="flex w-full max-w-fit items-center overflow-hidden">
                  <Avatar className="mr-2 size-7 shrink-0 rounded-full md:hidden">
                    <AvatarImage
                      src={
                        row.original.avatar
                          ? `/avatars/${row.original.avatar}`
                          : ""
                      }
                      alt={`Avatar for ${row.original.name}`}
                    />
                    <AvatarFallback className="rounded-full bg-card pb-0.5 text-xs font-medium text-muted">
                      {getInitials(row.original.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span
                    className="cursor-pointer text-sm no-underline"
                    onClick={handleCopy}
                    onMouseEnter={() => {
                      if (resetTimerRef.current) {
                        clearTimeout(resetTimerRef.current);
                      }
                    }}
                    onMouseLeave={() => {
                      setOpen(false);
                      setOriginalState();
                    }}
                  >
                    {row.original.email}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" align="center" className={tooltipBg}>
                {tooltipText}
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      );
    },
  },
  {
    accessorKey: "lastSignInAt",
    header: () => (
      <div className="flex items-center gap-1 whitespace-nowrap">
        <Clock size={14} className="mt-0.5 text-muted" />
        Last Login
      </div>
    ),
    cell: ({ row }) => {
      const days = daysAgo(new Date(row.original.lastSignInAt || ""));

      return (
        <div className="flex items-center gap-2">
          <span className="text-sm">
            {row.original.lastSignInAt
              ? days === 0
                ? "Today"
                : `${days} ${days === 1 ? "Day" : "Days"} Ago`
              : "Never"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "plusSubscription",
    header: () => (
      <div className="flex items-center gap-1 whitespace-nowrap">
        <PowerOnPro className="mt-0.5 size-4 fill-muted" />
        <sup className="text-brand-accent">+</sup> Subscription
      </div>
    ),
    cell: ({ row }) => {
      const [isBlocked, setIsBlocked] = useState(false);
      const isLoadingThisRow = loadingMemberId === row.original.id;
      const debouncedToggleSubscription = useDebounce(
        onToggleSubscription,
        DEFAULT_DEBOUNCE_DELAY,
        () => {
          setIsBlocked(true);
          setTimeout(() => setIsBlocked(false), TAILWIND_SHAKE_ANIMATION_DELAY);
        },
      );

      return (
        <div className="relative flex items-center">
          {isLoadingThisRow && (
            <LoaderCircle className="absolute -left-6 mb-0.5 size-4 animate-spin text-brand-primary" />
          )}
          <span className="text-sm">
            <Switch
              checked={row.original.isPowerOnProPlus}
              onCheckedChange={(checked) =>
                debouncedToggleSubscription(row.original, checked)
              }
              className={isBlocked ? "animate-shake" : ""}
              disabled={isLoadingThisRow}
            />
          </span>
        </div>
      );
    },
  },
];
